import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import Section from '~/constants/vdp/section';

type ContextType = {
  sectionsMounted: Partial<Record<Section, boolean>>;
  sectionsViewed: Partial<Record<Section, boolean>>;
  setSectionMounted: (section: Section) => void;
  setSectionViewed: (section: Section) => void;
};

const defaultContext: ContextType = {
  sectionsMounted: {
    [Section.Hero]: true,
    [Section.Inspection]: false,
    [Section.OwnerReviews]: false,
    [Section.PriceDetails]: false,
    [Section.RecommendedVehicles]: false,
    [Section.Spinner]: true,
    [Section.VehicleDetails]: false
  },
  sectionsViewed: {
    [Section.Gallery]: false,
    [Section.RecommendedVehicles]: false
  },
  setSectionMounted: () => {},
  setSectionViewed: () => {}
};

const UiContext = createContext<ContextType>(defaultContext);

export function UiContextProvider({ children }: React.PropsWithChildren<{}>) {
  const [sectionsViewed, setSectionsViewed] = useState(
    defaultContext.sectionsViewed
  );
  const [sectionsMounted, setSectionsMounted] = useState(
    defaultContext.sectionsMounted
  );

  const setSectionViewed = useCallback((section: Section) => {
    setSectionsViewed(prev => ({ ...prev, [section]: true }));
  }, []);

  const setSectionMounted = useCallback((section: Section) => {
    setSectionsMounted(prev => ({ ...prev, [section]: true }));
  }, []);

  const value = useMemo(
    () => ({
      sectionsViewed,
      setSectionViewed,
      sectionsMounted,
      setSectionMounted
    }),
    [sectionsViewed, setSectionViewed, sectionsMounted, setSectionMounted]
  );

  return <UiContext.Provider value={value}>{children}</UiContext.Provider>;
}

UiContextProvider.displayName = 'UiContextProvider';

export default UiContext;

export function useSectionViewed(section: Section) {
  return useContext(UiContext).sectionsViewed[section] ?? false;
}

export function useSectionMounted(section: Section) {
  return useContext(UiContext).sectionsMounted[section] ?? false;
}
